import type { FC, FormEvent, ReactNode } from 'react'
import type { Plan } from '@/types'

import { useEffect, useState } from 'react'
import { formatRupiah } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormField, fieldMessageId } from '@/components/dashboard/form-field'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

interface EditPriceDialogProps {
    plan: Plan | null
    onOpenChange: (open: boolean) => void
    onSave: (plan: Plan, prices: Pick<Plan, 'price' | 'annualPrice'>) => void
}

const MIN_PRICE = 50000

const parsePrice = (value: string): number => Number(value.replace(/[^0-9]/g, ''))

export const EditPriceDialog: FC<EditPriceDialogProps> = ({ plan, onOpenChange, onSave }): ReactNode => {
    const [price, setPrice] = useState('')
    const [annualPrice, setAnnualPrice] = useState('')
    const [errors, setErrors] = useState<{ price?: string, annualPrice?: string }>({})

    useEffect(() => {
        if (!plan) return

        setPrice(String(plan.price))
        setAnnualPrice(String(plan.annualPrice))
        setErrors({})
    }, [plan])

    const monthly = parsePrice(price)
    const annual = parsePrice(annualPrice)
    const discount = monthly > 0 && annual > 0 ? Math.round((1 - annual / monthly) * 100) : 0

    const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        if (!plan) return

        const next: { price?: string, annualPrice?: string } = {}

        if (!monthly) next.price = 'Harga bulanan wajib diisi.'
        else if (monthly < MIN_PRICE) next.price = `Harga minimal ${formatRupiah(MIN_PRICE)}.`

        if (!annual) next.annualPrice = 'Harga tahunan wajib diisi.'
        else if (annual < MIN_PRICE) next.annualPrice = `Harga minimal ${formatRupiah(MIN_PRICE)}.`
        else if (monthly && annual > monthly) next.annualPrice = 'Harga tahunan per bulan tidak boleh lebih mahal dari harga bulanan.'

        setErrors(next)
        if (next.price || next.annualPrice) return

        onSave(plan, { price: monthly, annualPrice: annual })
        onOpenChange(false)
    }

    return (
        <Dialog open={plan !== null} onOpenChange={onOpenChange}>
            <DialogContent className='sm:max-w-md'>
                <form onSubmit={onSubmit} noValidate className='grid gap-5'>
                    <DialogHeader>
                        <DialogTitle>
                            Ubah harga paket {plan?.name}
                        </DialogTitle>
                        <DialogDescription>
                            Harga baru berlaku untuk pelanggan baru. Pelanggan lama tetap memakai harga lamanya sampai periode berikutnya.
                        </DialogDescription>
                    </DialogHeader>

                    <div className='grid gap-4'>
                        <FormField id='plan-price' label='Harga bulanan (Rp)' required error={errors.price}>
                            <Input
                                id='plan-price'
                                inputMode='numeric'
                                value={price}
                                onChange={(event) => {
                                    setPrice(event.target.value)
                                    setErrors((current) => ({ ...current, price: undefined }))
                                }}
                                aria-invalid={Boolean(errors.price)}
                                aria-describedby={errors.price ? fieldMessageId('plan-price') : undefined}
                            />
                        </FormField>

                        <FormField
                            id='plan-annual-price'
                            label='Harga per bulan jika tahunan (Rp)'
                            required
                            error={errors.annualPrice}
                            hint={discount > 0 ? `Hemat ${discount}% dibanding bulanan.` : 'Biasanya lebih murah dari harga bulanan.'}
                        >
                            <Input
                                id='plan-annual-price'
                                inputMode='numeric'
                                value={annualPrice}
                                onChange={(event) => {
                                    setAnnualPrice(event.target.value)
                                    setErrors((current) => ({ ...current, annualPrice: undefined }))
                                }}
                                aria-invalid={Boolean(errors.annualPrice)}
                                aria-describedby={fieldMessageId('plan-annual-price')}
                            />
                        </FormField>
                    </div>

                    <DialogFooter>
                        <DialogClose render={<Button type='button' variant='outline' />}>
                            Batal
                        </DialogClose>
                        <Button type='submit'>
                            Simpan harga
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
